import { motion } from "framer-motion";
import { TrendingUp, Sparkles } from "lucide-react";
import { PrimaryCTA, SecondaryCTA } from "../CTAButton";
import { IMAGES } from "../../data/content";

const line = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: 0.1 + i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

const HEADLINE = ["Turn Your Data Into", "an Intelligent Business", "Advantage."];

export default function Hero() {
  return (
    <section className="relative overflow-hidden bg-white pt-32 pb-16 lg:pt-40 lg:pb-24" data-testid="hero">
      <div className="absolute inset-0 grid-pattern-light opacity-50" aria-hidden="true" />
      <div className="ad-container relative">
        <div className="grid items-center gap-12 lg:grid-cols-[1fr_0.95fr]">
          {/* Left */}
          <div>
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.05 }}
              className="inline-flex items-center gap-2 rounded-full border border-[#111827]/10 bg-[#F3F5F7] px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-[#1677FF]"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#00C7D9]" />
              AI · Data · Analytics · Cloud
            </motion.span>

            <h1 className="mt-6 display-xl text-[#111827]">
              {HEADLINE.map((l, i) => (
                <motion.span key={i} className="block" variants={line} custom={i} initial="hidden" animate="visible">
                  {i === 2 ? <span className="text-gradient">{l}</span> : l}
                </motion.span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.5 }}
              className="mt-7 max-w-xl text-base leading-relaxed text-[#4B5563] sm:text-lg"
            >
              We engineer data platforms, AI solutions and analytics systems that help
              organizations make smarter decisions, automate operations and create
              measurable business value.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45, duration: 0.5 }}
              className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center"
            >
              <PrimaryCTA to="/consultation" testId="hero-book-consultation">Book a Consultation</PrimaryCTA>
              <SecondaryCTA to="/solutions" testId="hero-explore-solutions">Explore Our Solutions</SecondaryCTA>
            </motion.div>
          </div>

          {/* Right visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.35, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <div className="relative overflow-hidden rounded-[1.75rem] border border-[#111827]/8 shadow-[0_40px_80px_-40px_rgba(17,24,39,0.4)]">
              <div className="img-overlay-blue relative">
                <img
                  src={IMAGES.heroTeam}
                  alt="A business team collaborating with data and technology"
                  className="h-[420px] w-full object-cover sm:h-[500px] lg:h-[560px]"
                />
              </div>

              {/* Annotation: top */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="absolute left-5 top-5 flex items-center gap-2 rounded-full bg-white/95 px-4 py-2 backdrop-blur"
              >
                <Sparkles className="h-4 w-4 text-[#1677FF]" />
                <span className="text-xs font-semibold text-[#111827]">Human expertise + AI</span>
              </motion.div>

              {/* Annotation: metric card */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="absolute bottom-5 left-5 right-5 rounded-2xl border border-white/40 bg-white/95 p-5 backdrop-blur"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-[#6B7280]">From data to decisions</p>
                    <p className="mt-1 text-base font-bold text-[#111827]">Engineered intelligence, in production</p>
                  </div>
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#1677FF]/10">
                    <TrendingUp className="h-5 w-5 text-[#1677FF]" />
                  </span>
                </div>
                <div className="mt-4 flex gap-1.5">
                  {[40, 62, 50, 78, 68, 90].map((h, i) => (
                    <motion.div
                      key={i}
                      initial={{ height: 4 }}
                      animate={{ height: `${h * 0.32}px` }}
                      transition={{ delay: 0.7 + i * 0.05, duration: 0.4 }}
                      className="flex-1 self-end rounded-sm bg-gradient-to-t from-[#1677FF] to-[#00C7D9]"
                    />
                  ))}
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
